import { Fragment } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import type {
  ProductionCostClosingCard,
  ProductionCostClosingRow,
  ProductionCostClosingSum,
  Value,
} from '@/utils/approval/productionCostClosing';

/** 레거시 &nbsp; */
const NBSP = '\u00a0';

/** 저장 HTML 의 상세보기 버튼 — 레거시 문서와 같은 class·data 이름이라 조회 변환을 같이 씀 */
export const DETAIL_BUTTON_CLASS = 'btn_detail_show';

export const text = (v: unknown) =>
  v === null || v === undefined ? '' : String(v).trim();

export const toNumber = (v: unknown) => Number(text(v).replace(/,/g, '')) || 0;

/** 레거시 number_format. 값이 안 오면 0 */
export const formatNumber = (v: unknown, decimals = 0) => {
  const raw = text(v);
  if (!raw) return (0).toFixed(decimals);
  const n = Number(raw.replace(/,/g, ''));
  return Number.isFinite(n)
    ? n.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : raw;
};

const isDetailRow = (row: ProductionCostClosingRow) => text(row.chk) === '1';

type Group<T> = { key: string; items: T[] };

/** 레거시 SEASON_ROWSPAN·DIVISION_ROWSPAN — 연속한 같은 값끼리 */
const groupBy = <T,>(items: T[], keyOf: (item: T) => string): Group<T>[] =>
  items.reduce<Group<T>[]>((groups, item) => {
    const key = keyOf(item);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(item);
    else groups.push({ key, items: [item] });
    return groups;
  }, []);

const seasonKey = (row: ProductionCostClosingRow) => text(row.seasonName);
const divisionKey = (row: ProductionCostClosingRow) =>
  text(row.division) || text(row.divisionName);

type RowCell = { first: boolean; rowSpan: number };

/** 행마다 시즌·구분 칸을 그릴지(그룹 첫 행) */
const layoutRows = (rows: ProductionCostClosingRow[]) =>
  groupBy(rows, seasonKey).map((season) => {
    const division = new Map<ProductionCostClosingRow, RowCell>();
    groupBy(season.items, divisionKey).forEach((g) =>
      g.items.forEach((row, i) =>
        division.set(row, { first: i === 0, rowSpan: g.items.length }),
      ),
    );
    return { rows: season.items, division };
  });

const DetailButton = ({
  row,
  approvalId,
  label,
}: {
  row: ProductionCostClosingRow;
  approvalId: string;
  label: string;
}) => (
  <button
    type="button"
    className={DETAIL_BUTTON_CLASS}
    data-reqymd={text(row.reqYmd)}
    data-maker={text(row.maker)}
    data-division={text(row.division)}
    data-tax_no={text(row.taxNo)}
    data-aprv_id={approvalId}
    data-inv_id={text(row.invId)}
  >
    {label}
  </button>
);

const N_COL_WIDTHS = [
  '8%',
  '9%',
  '12%',
  '12%',
  '6%',
  '12%',
  '12%',
  '10%',
  '11%',
  '8%',
];

const NHeader = ({ title, t }: { title: string; t: TFunction }) => (
  <>
    <colgroup>
      {N_COL_WIDTHS.map((w, i) => (
        <col key={i} width={w} />
      ))}
    </colgroup>
    <tbody>
      <tr>
        <th colSpan={10} className="text-center">
          {title}
        </th>
      </tr>
      <tr>
        <th className="text-center" rowSpan={2}>
          {t('시즌')}
        </th>
        <th className="text-center" rowSpan={2}>
          {t('구분')}
        </th>
        <th className="text-center" rowSpan={2}>
          {t('사업자 등록증 번호')}
        </th>
        <th className="text-center" rowSpan={2}>
          {t('업체명')}
        </th>
        <th className="text-center" rowSpan={2}>
          {t('화폐 단위')}
        </th>
        <th className="text-center" colSpan={2}>
          {t('공급가액')}
        </th>
        <th className="text-center" rowSpan={2}>
          {t('부가세')}
        </th>
        <th className="text-center" rowSpan={2}>
          {t('합계금액(원화)')}
        </th>
        <th className="text-center" rowSpan={2}>
          {t('마감내역')}
        </th>
      </tr>
      <tr>
        <th className="text-center">{t('화폐')}</th>
        <th className="text-center">{t('원화')}</th>
      </tr>
    </tbody>
  </>
);

type SumRowProps = {
  label: string;
  className: string;
  supply: Value;
  reqAmt: Value;
  reqVat: Value;
  settleAmt: Value;
};

const SumRow = ({
  label,
  className,
  supply,
  reqAmt,
  reqVat,
  settleAmt,
}: SumRowProps) => (
  <tr>
    <td colSpan={5} className={`${className} text-center textBold`}>
      {label}
    </td>
    <td className={`${className} text-right textBold`}>
      {formatNumber(supply)}
    </td>
    <td className={`${className} text-right textBold`}>
      {formatNumber(reqAmt)}
    </td>
    <td className={`${className} text-right textBold`}>
      {formatNumber(reqVat)}
    </td>
    <td className={`${className} text-right textBold`}>
      {formatNumber(settleAmt)}
    </td>
    <td className={className} />
  </tr>
);

/** 레거시 일반(N) 정산표 행 + 시즌 소계. 소계는 구분 소계(chk 2) 행만 더함 */
const NRows = ({
  rows,
  approvalId,
  t,
}: {
  rows: ProductionCostClosingRow[];
  approvalId: string;
  t: TFunction;
}) => (
  <>
    {layoutRows(rows).map((season, si) => {
      let supplySum = 0;
      let count = 0;
      let reqAmt = 0;
      let reqVat = 0;
      let settle = 0;
      const body = season.rows.map((row, ri) => {
        const detail = isDetailRow(row);
        const cls = detail ? '' : 'bg-success textBold';
        if (!detail) {
          count += toNumber(row.gubunCount);
          reqAmt += toNumber(row.reqAmt);
          reqVat += toNumber(row.reqVat);
          settle += toNumber(row.settleAmt);
          supplySum += toNumber(row.supplyAmtCurr);
        }
        const division = season.division.get(row);
        return (
          <tr key={ri}>
            {ri === 0 && (
              <td
                className="bg-warning text-center textBold"
                rowSpan={season.rows.length + 1}
              >
                {text(row.seasonName)}
              </td>
            )}
            {division?.first && (
              <td className="text-center" rowSpan={division.rowSpan}>
                {text(row.divisionName)}
              </td>
            )}
            <td className={`text-center ${cls}`}>
              {detail
                ? text(row.busiNo)
                : t('{{count}}건', { count: toNumber(row.gubunCount) })}
            </td>
            <td className={`${detail ? 'text-left' : 'text-center'} ${cls}`}>
              {detail ? (
                <>
                  {text(row.custName)}
                  <br />({text(row.maker)})
                </>
              ) : (
                text(row.divisionName)
              )}
            </td>
            <td className={`text-center ${cls}`}>
              {detail ? text(row.cur) : ''}
            </td>
            <td className={`text-right ${cls}`}>
              {formatNumber(row.supplyAmtCurr)}
            </td>
            <td className={`text-right ${cls}`}>{formatNumber(row.reqAmt)}</td>
            <td className={`text-right ${cls}`}>{formatNumber(row.reqVat)}</td>
            <td className={`text-right ${cls}`}>
              {formatNumber(row.settleAmt)}
            </td>
            <td className={`text-center ${cls}`}>
              {detail && (
                <DetailButton
                  row={row}
                  approvalId={approvalId}
                  label={t('상세보기')}
                />
              )}
            </td>
          </tr>
        );
      });
      return (
        <Fragment key={si}>
          {body}
          <tr className="bg-danger text-center textBold">
            <td colSpan={4} className="bg-danger">
              {t('시즌 소계 {{value}}건', { value: formatNumber(count) })}
            </td>
            <td className="text-right textBold bg-danger">
              {formatNumber(supplySum)}
            </td>
            <td className="text-right textBold bg-danger">
              {formatNumber(reqAmt)}
            </td>
            <td className="text-right textBold bg-danger">
              {formatNumber(reqVat)}
            </td>
            <td className="text-right textBold bg-danger">
              {formatNumber(settle)}
            </td>
            <td className="text-right textBold bg-danger" />
          </tr>
        </Fragment>
      );
    })}
  </>
);

/** 레거시 관부가세(T)·부대비(E) 국내 표 — 상세보기 없음 */
const EtTable = ({
  rows,
  title,
  isTax,
  t,
}: {
  rows: ProductionCostClosingRow[];
  title: string;
  isTax: boolean;
  t: TFunction;
}) => {
  const widths = isTax
    ? ['7%', '7%', '8%', '8%', '15%', '5%', '8%', '10%', '8%', '9%', '15%']
    : ['7%', '7%', '13%', '10%', '12%', '5%', '11%', '8%', '9%', '13%'];
  return (
    <table>
      <colgroup>
        {widths.map((w, i) => (
          <col key={i} width={w} />
        ))}
      </colgroup>
      <tbody>
        <tr>
          <th colSpan={widths.length} className="text-center">
            {title}
          </th>
        </tr>
        <tr>
          <th className="text-center">{t('시즌')}</th>
          <th className="text-center">{t('구분')}</th>
          <th className="text-center">BL</th>
          <th className="text-center">{t('사업자 등록증 번호')}</th>
          <th className="text-center">{t('업체명')}</th>
          <th className="text-center">{t('화폐 단위')}</th>
          <th className="text-center">{t('물품대금액(USD)')}</th>
          {isTax && (
            <th className="text-center">{t('부가가치세 과표 금액')}</th>
          )}
          <th className="text-center">{isTax ? t('관세') : t('공급가액')}</th>
          <th className="text-center">{t('부가세')}</th>
          <th className="text-center">
            {isTax
              ? t('합계금액(관/부가세 합계금액)')
              : t('합계금액(공급가액/부가세 합계금액)')}
          </th>
        </tr>
        {layoutRows(rows).map((season, si) => {
          let invoice = 0;
          let amtCurr = 0;
          let reqSum = 0;
          let vatSum = 0;
          let totalSum = 0;
          return (
            <Fragment key={si}>
              {season.rows.map((row, ri) => {
                const detail = isDetailRow(row);
                const cls = detail ? '' : 'bg-success textBold';
                invoice += toNumber(row.prdAmtCurr);
                amtCurr += toNumber(row.reqAmt);
                reqSum += toNumber(isTax ? row.reqVat : row.vatAmtE);
                vatSum += toNumber(isTax ? row.vatAmt : row.supplyAmtCurr);
                totalSum += toNumber(isTax ? row.total : row.sumCurr);
                const division = season.division.get(row);
                return (
                  <tr key={ri}>
                    {ri === 0 && (
                      <td
                        className="bg-warning text-center textBold"
                        rowSpan={season.rows.length + 1}
                      >
                        {text(row.seasonName)}
                      </td>
                    )}
                    {division?.first && (
                      <td className="text-center" rowSpan={division.rowSpan}>
                        {text(row.divisionName)}
                      </td>
                    )}
                    <td
                      className={`text-center ${cls}`}
                      colSpan={detail ? undefined : 2}
                    >
                      {detail
                        ? text(row.blNo)
                        : t('{{count}}건', { count: toNumber(row.gubunCount) })}
                    </td>
                    {detail && (
                      <td className="text-center">{text(row.busiNo)}</td>
                    )}
                    <td className={`text-center ${cls}`}>
                      {detail
                        ? `${text(row.custName)}${NBSP}(${text(row.maker)})`
                        : text(row.divisionName)}
                    </td>
                    <td className={`text-center ${cls}`}>
                      {detail ? text(row.cur) : ''}
                    </td>
                    <td className={`text-right ${cls}`}>
                      {formatNumber(row.prdAmtCurr, 2)}
                    </td>
                    {isTax && (
                      <td className={`text-right ${cls}`}>
                        {formatNumber(row.reqAmt)}
                      </td>
                    )}
                    <td className={`text-right ${cls}`}>
                      {formatNumber(isTax ? row.vatAmt : row.supplyAmtCurr)}
                    </td>
                    <td className={`text-right ${cls}`}>
                      {formatNumber(isTax ? row.reqVat : row.vatAmtE)}
                    </td>
                    <td className={`text-right ${cls}`}>
                      {formatNumber(isTax ? row.total : row.sumCurr)}
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-danger text-center textBold">
                <td colSpan={5} className="bg-danger">
                  {t('시즌 소계')}
                </td>
                <td className="text-right textBold bg-danger">
                  {formatNumber(invoice, 2)}
                </td>
                {isTax && (
                  <td className="text-right textBold bg-danger">
                    {formatNumber(amtCurr)}
                  </td>
                )}
                <td className="text-right textBold bg-danger">
                  {formatNumber(vatSum)}
                </td>
                <td className="text-right textBold bg-danger">
                  {formatNumber(reqSum, isTax ? 2 : 0)}
                </td>
                <td className="text-right textBold bg-danger">
                  {formatNumber(totalSum)}
                </td>
              </tr>
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

const sumCount = (sum: ProductionCostClosingSum | null | undefined) =>
  toNumber(sum?.total);

/** 국내 + 직송. 둘 다 값이 없으면 빈칸 */
const addSum = (a: Value, b: Value): Value =>
  text(a) || text(b) ? toNumber(a) + toNumber(b) : undefined;

/**
 * 레거시 appr_sub_view_forms_tax_month_view_mstr.
 * 생산비 마감 총계는 국내 + 직송(레거시는 직송만 더하던 오류), 레거시처럼 직송 표가 있을 때만.
 * 관부가세·부대비 국내 표는 열 구성이 달라 총계 줄 없음
 */
const ProductionCostClosingContentView = ({
  card,
  t,
}: {
  card: ProductionCostClosingCard;
  t: TFunction;
}) => {
  const mode = text(card.divisionMode);
  const approvalId = text(card.aprvId);
  const title = text(card.titles?.[0]);
  const rows = card.rows ?? [];
  const rowsL = card.rowsL ?? [];
  const sum = card.sum;
  const sumL = card.sumL;
  const isEt = mode === 'T' || mode === 'E';
  const grandTotal = (
    <SumRow
      label={t('생산비 마감 총계 {{value}}건', {
        value: formatNumber(sumCount(sum) + sumCount(sumL)),
      })}
      className="bg-warning"
      supply={addSum(sum?.sumSupplyAmtCurr, sumL?.sumSupplyAmtCurr)}
      reqAmt={addSum(sum?.sumReqAmt, sumL?.sumReqAmt)}
      reqVat={addSum(sum?.sumReqVat, sumL?.sumReqVat)}
      settleAmt={addSum(sum?.sumSettleAmt, sumL?.sumSettleAmt)}
    />
  );
  return (
    <>
      {rows.length > 0 && (
        <div>
          {isEt ? (
            <EtTable rows={rows} title={title} isTax={mode === 'T'} t={t} />
          ) : (
            <table>
              <NHeader title={title} t={t} />
              <tbody>
                <NRows rows={rows} approvalId={approvalId} t={t} />
                <SumRow
                  label={t('국내거래(내수) 총계 {{value}}건', {
                    value: formatNumber(sumCount(sum)),
                  })}
                  className="bg-info"
                  supply={sum?.sumSupplyAmtCurr}
                  reqAmt={sum?.sumReqAmt}
                  reqVat={sum?.sumReqVat}
                  settleAmt={sum?.sumSettleAmt}
                />
              </tbody>
            </table>
          )}
        </div>
      )}
      {rowsL.length > 0 && (
        <div>
          <table>
            <NHeader title={t('국외거래(직송)')} t={t} />
            <tbody>
              <NRows rows={rowsL} approvalId={approvalId} t={t} />
              <SumRow
                label={t('국외거래(직송) 총계 {{value}}건', {
                  value: formatNumber(sumCount(sumL)),
                })}
                className="bg-info"
                supply={sumL?.sumSupplyAmtCurr}
                reqAmt={sumL?.sumReqAmt}
                reqVat={sumL?.sumReqVat}
                settleAmt={sumL?.sumSettleAmt}
              />
              {grandTotal}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

/** attributes.content — 조회 화면은 이 HTML 을 그대로 보여 줌 */
export const buildProductionCostClosingContentHtml = (
  card: ProductionCostClosingCard,
  t: TFunction,
): string =>
  renderToStaticMarkup(<ProductionCostClosingContentView card={card} t={t} />);
